import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import {
  ErrorOutlineRounded,
  HourglassTopRounded,
  CancelRounded,
  ChatBubbleOutlineRounded,
  ShieldOutlined,
} from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CWY_COLORS } from '../theme/colors';
import { getStripePaymentStatus } from '../services/paymentStatusService';
import { playPaymentSuccessSound } from '../utils/playPaymentSuccessSound';
import { PaymentSuccessCheck } from '../components/payment/PaymentSuccessCheck';
import { markPaymentResultSeen } from '../utils/paymentResultSeen';

type ResultOutcome = 'loading' | 'pending' | 'success' | 'cancelled' | 'failed';

const POLL_MS = 2000;
const POLL_MAX_MS = 45000;
/** Hold on success card before returning to chat */
const SUCCESS_REDIRECT_MS = 6500;

const displayFont = "'Space Grotesk', 'Plus Jakarta Sans', system-ui, sans-serif";
const bodyFont = "'Plus Jakarta Sans', Inter, system-ui, sans-serif";
const monoFont = "'IBM Plex Mono', ui-monospace, Menlo, monospace";

function formatMoney(amount: number, currency: string): string {
  const code = (currency || 'CAD').toUpperCase();
  const symbol = code === 'USD' ? '$' : code === 'INR' ? '₹' : 'C$';
  return `${symbol}${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function mapStatusToOutcome(status: string, urlHint?: string | null): ResultOutcome {
  const s = (status || '').toLowerCase();
  if (s === 'confirmed' || s === 'verified' || s === 'paid') return 'success';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'failed') return 'failed';
  if (urlHint === 'cancelled') return 'cancelled';
  return 'pending';
}

const statusCopy: Record<
  ResultOutcome,
  { eyebrow: string; title: string; subtitle: string; accent: string; glow: string }
> = {
  loading: {
    eyebrow: 'Secure checkout',
    title: 'Verifying payment',
    subtitle: 'Confirming your payment with our servers…',
    accent: CWY_COLORS.red,
    glow: 'rgba(204, 27, 27, 0.28)',
  },
  pending: {
    eyebrow: 'Almost there',
    title: 'Confirming payment',
    subtitle: 'This usually takes a few seconds. Hang tight.',
    accent: '#E8A317',
    glow: 'rgba(232, 163, 23, 0.3)',
  },
  success: {
    eyebrow: 'Transaction complete',
    title: 'Payment successful',
    subtitle: 'Your payment has been completed successfully.',
    accent: CWY_COLORS.verified,
    glow: 'rgba(30, 157, 99, 0.35)',
  },
  cancelled: {
    eyebrow: 'No charge made',
    title: 'Payment cancelled',
    subtitle: 'You left checkout before completing payment.',
    accent: CWY_COLORS.textSecondary,
    glow: 'rgba(92, 93, 99, 0.22)',
  },
  failed: {
    eyebrow: 'Needs attention',
    title: 'Couldn’t verify payment',
    subtitle: 'Return to chat and try again, or contact support.',
    accent: CWY_COLORS.red,
    glow: 'rgba(204, 27, 27, 0.3)',
  },
};

const FloatingOrb: React.FC<{
  size: number;
  top: string;
  left?: string;
  right?: string;
  color: string;
  delay?: number;
  reduceMotion: boolean | null;
}> = ({ size, top, left, right, color, delay = 0, reduceMotion }) => (
  <motion.div
    aria-hidden
    style={{
      position: 'absolute',
      top,
      left,
      right,
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      filter: 'blur(40px)',
      pointerEvents: 'none',
    }}
    animate={
      reduceMotion
        ? { opacity: 0.55 }
        : { opacity: [0.35, 0.7, 0.35], y: [0, -18, 0], scale: [1, 1.08, 1] }
    }
    transition={{ duration: 7 + delay, repeat: Infinity, ease: 'easeInOut', delay }}
  />
);

/** Green check pop on the result card — shared PaymentSuccessCheck. */

const StatusGlyph: React.FC<{ outcome: ResultOutcome; accent: string }> = ({
  outcome,
  accent,
}) => {
  const reduceMotion = useReducedMotion();

  if (outcome === 'success') {
    return (
      <Box sx={{ mb: 1 }}>
        <PaymentSuccessCheck size="lg" />
      </Box>
    );
  }

  const iconSx = { fontSize: 52, color: '#fff' };
  const glyph =
    outcome === 'cancelled' ? (
      <CancelRounded sx={iconSx} />
    ) : outcome === 'failed' ? (
      <ErrorOutlineRounded sx={iconSx} />
    ) : outcome === 'pending' ? (
      <HourglassTopRounded sx={iconSx} />
    ) : (
      <ShieldOutlined sx={iconSx} />
    );

  return (
    <Box sx={{ position: 'relative', width: 112, height: 112, mx: 'auto', mb: 1 }}>
      {!reduceMotion && (
        <motion.div
          aria-hidden
          style={{
            position: 'absolute',
            inset: -10,
            borderRadius: '50%',
            background: `conic-gradient(from 180deg, ${accent}, transparent 55%)`,
            opacity: 0.45,
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
      )}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(145deg, ${accent} 0%, ${accent}cc 55%, #0f172a 140%)`,
          boxShadow: `0 18px 40px ${accent}55, inset 0 1px 0 rgba(255,255,255,0.35)`,
          transformStyle: 'preserve-3d',
        }}
        initial={reduceMotion ? false : { scale: 0.6, rotateX: 28, opacity: 0 }}
        animate={{ scale: 1, rotateX: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      >
        <motion.div
          animate={
            outcome === 'pending' || outcome === 'loading'
              ? reduceMotion
                ? undefined
                : { rotate: [0, 8, -8, 0] }
              : undefined
          }
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          {glyph}
        </motion.div>
      </motion.div>
    </Box>
  );
};

export const PaymentResultPage: React.FC = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';
  const threadId = searchParams.get('thread_id') || '';
  const transactionIdParam = searchParams.get('transaction_id') || '';
  const outcomeHint = searchParams.get('outcome');

  const [outcome, setOutcome] = useState<ResultOutcome>(
    outcomeHint === 'cancelled' ? 'cancelled' : 'loading'
  );
  const [amountLabel, setAmountLabel] = useState<string | null>(null);
  const [currencyCode, setCurrencyCode] = useState('CAD');
  const [transactionId, setTransactionId] = useState(transactionIdParam);
  const [payeeLabel, setPayeeLabel] = useState<string | null>(null);
  const [detailOverride, setDetailOverride] = useState<string | null>(null);
  const [redirectSecondsLeft, setRedirectSecondsLeft] = useState<number | null>(null);

  const soundPlayedRef = useRef(false);
  const pollStartedAtRef = useRef<number>(0);
  const redirectTimerRef = useRef<number | undefined>(undefined);
  const countdownTimerRef = useRef<number | undefined>(undefined);

  const chatPath = useMemo(() => {
    if (!threadId) return '/chat';
    return `/chat?thread=${encodeURIComponent(threadId)}`;
  }, [threadId]);

  const goToChat = useCallback(() => {
    markPaymentResultSeen(transactionId || transactionIdParam);
    navigate(chatPath, { replace: true });
  }, [navigate, chatPath, transactionId, transactionIdParam]);

  const copy = statusCopy[outcome];

  useEffect(() => {
    if (outcome !== 'success') {
      setRedirectSecondsLeft(null);
      if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
      if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
      return;
    }

    if (!soundPlayedRef.current) {
      soundPlayedRef.current = true;
      playPaymentSuccessSound();
    }

    const totalSec = Math.round(SUCCESS_REDIRECT_MS / 1000);
    setRedirectSecondsLeft(totalSec);
    const started = Date.now();
    countdownTimerRef.current = window.setInterval(() => {
      const left = Math.max(0, totalSec - Math.floor((Date.now() - started) / 1000));
      setRedirectSecondsLeft(left);
    }, 250);

    redirectTimerRef.current = window.setTimeout(() => {
      goToChat();
    }, SUCCESS_REDIRECT_MS);

    return () => {
      if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
      if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
    };
  }, [outcome, goToChat]);

  useEffect(() => {
    if (outcomeHint === 'cancelled' && !sessionId) {
      setOutcome('cancelled');
      setDetailOverride('You cancelled checkout. No charge was made.');
      return;
    }

    if (!sessionId) {
      setOutcome('failed');
      setDetailOverride('Missing payment session. Return to chat and try again.');
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    pollStartedAtRef.current = Date.now();

    const scheduleNextPoll = () => {
      const elapsed = Date.now() - pollStartedAtRef.current;
      if (elapsed >= POLL_MAX_MS) {
        setOutcome('pending');
        setDetailOverride(
          'Still confirming. You can return to chat — status will update when ready.'
        );
        return;
      }
      timeoutId = window.setTimeout(() => {
        void poll();
      }, POLL_MS);
    };

    const poll = async () => {
      try {
        const data = await getStripePaymentStatus(sessionId);
        if (cancelled) return;

        // Webhook lag: keep verifying — never flash failed before a definitive status.
        if (!data.success || !data.transaction) {
          setOutcome('pending');
          setDetailOverride(null);
          scheduleNextPoll();
          return;
        }

        const txn = data.transaction;
        setTransactionId(txn.transaction_id || transactionIdParam);
        setCurrencyCode((txn.transaction_currency || 'CAD').toUpperCase());
        setAmountLabel(formatMoney(txn.transaction_amount, txn.transaction_currency));

        const details = (txn.method_details || {}) as Record<string, unknown>;
        if (details.payee_type === 'service_provider' && details.provider_name) {
          setPayeeLabel(`Payment to ${String(details.provider_name)}`);
        } else if (details.source === 'chat_payment' || details.payee_type === 'platform') {
          setPayeeLabel('Payment to Canadawale Yar™');
        } else {
          setPayeeLabel(null);
        }

        const next = mapStatusToOutcome(txn.status, outcomeHint);
        setOutcome(next);
        setDetailOverride(null);

        if (next === 'success' || next === 'cancelled' || next === 'failed') {
          return;
        }

        scheduleNextPoll();
      } catch (err: unknown) {
        if (cancelled) return;
        const elapsed = Date.now() - pollStartedAtRef.current;
        if (elapsed >= POLL_MAX_MS) {
          const apiMsg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setOutcome('failed');
          setDetailOverride(apiMsg || 'Could not verify payment status.');
          return;
        }
        setOutcome('pending');
        setDetailOverride(null);
        scheduleNextPoll();
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [sessionId, outcomeHint, transactionIdParam]);

  const redirectProgress =
    outcome === 'success' && redirectSecondsLeft != null
      ? ((SUCCESS_REDIRECT_MS / 1000 - redirectSecondsLeft) / (SUCCESS_REDIRECT_MS / 1000)) * 100
      : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        p: 0,
        m: 0,
        background: `
          radial-gradient(1200px 600px at 10% -10%, rgba(204,27,27,0.12), transparent 55%),
          radial-gradient(900px 500px at 90% 0%, rgba(30,157,99,0.1), transparent 50%),
          linear-gradient(180deg, #F4F2EE 0%, #EDEAE4 100%)
        `,
      }}
    >
      <FloatingOrb
        size={220}
        top="8%"
        left="6%"
        color="rgba(204,27,27,0.18)"
        reduceMotion={reduceMotion}
      />
      <FloatingOrb
        size={260}
        top="55%"
        right="4%"
        color="rgba(30,157,99,0.16)"
        delay={1.2}
        reduceMotion={reduceMotion}
      />
      <FloatingOrb
        size={160}
        top="20%"
        right="22%"
        color="rgba(0,113,133,0.12)"
        delay={0.6}
        reduceMotion={reduceMotion}
      />

      <motion.div
        key="result-card"
        style={{
          width: '100%',
          height: '100%',
          flex: 1,
          display: 'flex',
        }}
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            width: '100%',
            height: '100%',
            borderRadius: 0,
            p: 0,
            background: `linear-gradient(145deg, rgba(255,255,255,0.55), rgba(255,255,255,0.2) 40%, ${copy.accent}22)`,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              borderRadius: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 3, sm: 4 },
              py: { xs: 4, sm: 5 },
              textAlign: 'center',
              background: `
                linear-gradient(165deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)
              `,
              backdropFilter: 'blur(18px)',
            }}
          >
            {/* Soft top sheen */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, transparent 28%)',
                pointerEvents: 'none',
              }}
            />

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 480,
                zIndex: 1,
              }}
            >
            <AnimatePresence mode="wait">
              <motion.div
                key={outcome}
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
              >
                <Typography
                  sx={{
                    fontFamily: bodyFont,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: copy.accent,
                    mb: 2.5,
                  }}
                >
                  {copy.eyebrow}
                </Typography>

                <StatusGlyph outcome={outcome} accent={copy.accent} />

                <motion.div
                  initial={reduceMotion || outcome !== 'success' ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: outcome === 'success' ? 0.45 : 0, duration: 0.35 }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: displayFont,
                      fontWeight: 700,
                      fontSize: { xs: '1.85rem', sm: '2.15rem' },
                      letterSpacing: '-0.03em',
                      lineHeight: 1.15,
                      color: CWY_COLORS.textPrimary,
                      mt: 2.5,
                      mb: 1,
                    }}
                  >
                    {copy.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: bodyFont,
                      fontSize: '0.98rem',
                      lineHeight: 1.55,
                      color: CWY_COLORS.textSecondary,
                      maxWidth: 340,
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    {detailOverride || copy.subtitle}
                  </Typography>
                </motion.div>

                {amountLabel && (
                  <motion.div
                    initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 220, damping: 18 }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 3,
                        py: 2,
                        mb: 2.5,
                        borderRadius: '18px',
                        background: 'rgba(19, 25, 33, 0.04)',
                        border: '1px solid rgba(19, 25, 33, 0.06)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
                        transform: 'translateZ(24px)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: bodyFont,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: CWY_COLORS.textFaint,
                        }}
                      >
                        Amount paid
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: displayFont,
                          fontWeight: 700,
                          fontSize: { xs: '2rem', sm: '2.35rem' },
                          letterSpacing: '-0.04em',
                          color: CWY_COLORS.darkNavy,
                          lineHeight: 1,
                        }}
                      >
                        {amountLabel}
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            color: CWY_COLORS.textSecondary,
                            verticalAlign: 'super',
                          }}
                        >
                          {currencyCode}
                        </Box>
                      </Typography>
                      {payeeLabel && (
                        <Typography
                          sx={{
                            fontFamily: bodyFont,
                            fontSize: 13,
                            fontWeight: 500,
                            color: CWY_COLORS.textSecondary,
                            mt: 0.5,
                          }}
                        >
                          {payeeLabel}
                        </Typography>
                      )}
                    </Box>
                  </motion.div>
                )}

                {transactionId && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.75,
                      py: 1,
                      mb: 3,
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(19,25,33,0.08)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: bodyFont,
                        fontSize: 11,
                        fontWeight: 600,
                        color: CWY_COLORS.textFaint,
                      }}
                    >
                      Ref
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        fontWeight: 500,
                        color: CWY_COLORS.textPrimary,
                      }}
                    >
                      {transactionId}
                    </Typography>
                  </Box>
                )}
              </motion.div>
            </AnimatePresence>

            {outcome === 'success' && (
              <Box sx={{ mb: 2.5, mt: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: bodyFont,
                      fontSize: 12,
                      fontWeight: 600,
                      color: CWY_COLORS.textSecondary,
                    }}
                  >
                    Returning to chat
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      fontWeight: 600,
                      color: CWY_COLORS.textPrimary,
                    }}
                  >
                    {redirectSecondsLeft ?? Math.round(SUCCESS_REDIRECT_MS / 1000)}s
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, redirectProgress))}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: 'rgba(19,25,33,0.06)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${CWY_COLORS.verified}, #34d399)`,
                    },
                  }}
                />
              </Box>
            )}

            {(outcome === 'loading' || outcome === 'pending') && !detailOverride?.includes('Still') && (
              <LinearProgress
                sx={{
                  mb: 2.5,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: 'rgba(19,25,33,0.06)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    bgcolor: copy.accent,
                  },
                }}
              />
            )}

            <motion.div whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<ChatBubbleOutlineRounded />}
                onClick={goToChat}
                sx={{
                  py: 1.4,
                  textTransform: 'none',
                  fontFamily: bodyFont,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: '14px',
                  bgcolor: CWY_COLORS.darkNavy,
                  boxShadow: '0 12px 28px rgba(19,25,33,0.22)',
                  '&:hover': {
                    bgcolor: '#1c2430',
                    boxShadow: '0 14px 32px rgba(19,25,33,0.28)',
                  },
                }}
              >
                {outcome === 'success' ? 'Back to chat now' : 'Back to chat'}
              </Button>
            </motion.div>

            <Typography
              sx={{
                mt: 2,
                fontFamily: bodyFont,
                fontSize: 11,
                color: CWY_COLORS.textFaint,
              }}
            >
              Secured by Stripe · Canadawale Yar™
            </Typography>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default PaymentResultPage;
